import React, { useState, useEffect } from "react";
import api from "../../api/api";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";

import SecurityIcon from "@mui/icons-material/Security";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

const VerifyCode = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { email } = location.state || {};

  useEffect(() => {
    if (!email) {
      setMessage(
        "No email found. Please return and request a new verification code."
      );
    }
  }, [email]);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (code.length !== 6) {
      setMessage("Please enter the complete 6-digit code.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await api.post("/verify-reset-code/", {
        email,
        code,
      });

      setMessage(res.data.message || "Verification successful.");

      setTimeout(() => {
        navigate("/resetPassword", {
          state: { email, code },
        });
      }, 1000);
    } catch (err) {
      setMessage(
        err.response?.data?.error ||
          "Invalid verification code."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        background: "#fff",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 4,
      }}
    >
      {/* LARGE BUBBLES */}

      <Box
        sx={{
          position: "absolute",
          width: { xs: 180, md: 320 },
          height: { xs: 180, md: 320 },
          borderRadius: "50%",
          background: "rgba(255,140,0,.14)",
          top: "5%",
          left: "-5%",
          animation: "float1 14s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 220, md: 360 },
          height: { xs: 220, md: 360 },
          borderRadius: "50%",
          background: "rgba(33,150,243,.12)",
          top: "55%",
          right: "-10%",
          animation: "float2 18s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 170, md: 260 },
          height: { xs: 170, md: 260 },
          borderRadius: "50%",
          background: "rgba(255,183,77,.18)",
          bottom: "-5%",
          left: "25%",
          animation: "float3 20s ease-in-out infinite",
        }}
      />

      {/* MEDIUM BUBBLES */}

      <Box
        sx={{
          position: "absolute",
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "rgba(144,164,174,.15)",
          top: "15%",
          right: "15%",
          animation: "float2 12s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: "rgba(255,140,0,.12)",
          bottom: "15%",
          left: "10%",
          animation: "float1 16s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 140,
          height: 140,
          borderRadius: "50%",
          background: "rgba(33,150,243,.12)",
          top: "40%",
          left: "8%",
          animation: "float3 15s ease-in-out infinite",
        }}
      />

      {/* STARS */}

      {[...Array(40)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: `${2 + (i % 3)}px`,
            height: `${2 + (i % 3)}px`,
            borderRadius: "50%",
            background:
              i % 4 === 0
                ? "#ff8c00"
                : i % 4 === 1
                ? "#2196f3"
                : i % 4 === 2
                ? "#ffb74d"
                : "#90a4ae",
            top: `${(i * 13) % 100}%`,
            left: `${(i * 17) % 100}%`,
            opacity: 0.6,
          }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        style={{
          width: "100%",
          maxWidth: "550px",
          zIndex: 2,
        }}
      >
        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: "30px",
            background: "rgba(255,255,255,.92)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,140,0,.12)",
            boxShadow:
              "0 20px 60px rgba(0,0,0,.08)",
          }}
        >
          <Box textAlign="center">
            <motion.div
              animate={{
                y: [0, -10, 0],
              }}
              transition={{
                repeat: Infinity,
                duration: 2.5,
              }}
            >
              <VerifiedUserIcon
                sx={{
                  fontSize: 80,
                  color: "#ff8c00",
                }}
              />
            </motion.div>

            <Typography
              variant="h4"
              fontWeight={800}
              mt={2}
            >
              Verify Reset Code
            </Typography>

            <Typography
              sx={{
                mt: 1,
                color: "#666",
              }}
            >
              Enter the 6-digit verification code sent to
            </Typography>

            <Typography
              sx={{
                fontWeight: 700,
                color: "#2196f3",
                mt: 1,
                wordBreak: "break-all",
              }}
            >
              {email}
            </Typography>
          </Box>

          <Alert
            severity="info"
            sx={{
              mt: 3,
              borderRadius: 3,
            }}
          >
            Your verification code expires after 10 minutes.
          </Alert>

          <Box
            component="form"
            onSubmit={handleVerify}
            sx={{ mt: 3 }}
          >
            <TextField
              fullWidth
              label="Verification Code"
              value={code}
              disabled={loading}
              inputProps={{
                maxLength: 6,
              }}
              onChange={(e) => {
                const value = e.target.value.replace(
                  /\D/g,
                  ""
                );
                setCode(value);
              }}
              sx={{
                mb: 3,
              }}
            />

            <Button
              fullWidth
              type="submit"
              disabled={loading}
              sx={{
                py: 1.7,
                borderRadius: "14px",
                fontWeight: 700,
                background:
                  "linear-gradient(135deg,#ff8c00,#ffb74d)",
                color: "#fff",

                "&:hover": {
                  background:
                    "linear-gradient(135deg,#ff9800,#ff8c00)",
                },
              }}
            >
              {loading ? (
                <CircularProgress
                  size={24}
                  sx={{ color: "#fff" }}
                />
              ) : (
                "Verify Code"
              )}
            </Button>

            <Button
              fullWidth
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/forgotPassword")}
              sx={{
                mt: 2,
                color: "#555",
              }}
            >
              Back
            </Button>
          </Box>

          {message && (
            <Typography
              textAlign="center"
              mt={3}
              color="#ff8c00"
              fontWeight={600}
            >
              {message}
            </Typography>
          )}

          <Box
            sx={{
              mt: 4,
              textAlign: "center",
              borderTop: "1px solid #eee",
              pt: 3,
            }}
          >
            <Typography
              sx={{
                color: "#777",
                fontSize: ".9rem",
              }}
            >
              🚢 Zuluwhiskey Shipping • Fast • Secure • Reliable
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default VerifyCode;



// import React, { useState, useEffect } from "react";
// import { Box, Typography } from "@mui/material";
// import api from "../../api/api";
// import { useLocation, useNavigate } from "react-router-dom";
// import "./Auth.css";

// const VerifyCode = () => {
//   const location = useLocation();  
//   const navigate = useNavigate();  
//   const [code, setCode] = useState("");
//   const [message, setMessage] = useState("");
//   const [loading, setLoading] = useState(false);

//   const { email } = location.state || {}; 

//   useEffect(() => {
//     if (!email) {
//       setMessage("No email found. Please try again.");
//     }
//   }, [email]);

//   const handleVerify = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     setMessage("");  

//     setTimeout(async () => {
//       try {
//         const res = await api.post("/verify-reset-code/", { email, code });
//         setMessage(res.data.message || "Code verified!");
//         navigate("/resetPassword", { state: { email, code } });
//       } catch (err) {
//         setMessage(err.response?.data?.error || "Invalid code");
//       } finally {
//         setLoading(false);
//       }
//     }, 3000);
//   };

//   return (
//     <div>
//       <Box
//         sx={{
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "flex-start",
//           minHeight: "auto",
//           backgroundColor: "#001f3f",
//           textAlign: "center",
//           width: "100%",
//           p: 2,
//         }}
//       >
//         <Typography
//           sx={{
//             color: "#ffffff",
//             fontSize: { xs: "12px", sm: "14px" }, 
//             mt: 0,
//             p: 2,
//             width: "100%",
//             maxWidth: "100%",
//           }}
//   >
//           <Box
//             component="span"
//             sx={{
//               backgroundColor: "#333333",
//               color: "white",
//               borderRadius: "4px",
//               px: 1.5,
//               py: 0.5,
//               display: "inline-block",
//             }}
//           >
//             Missiah Mission
//             <span style={{ color: "yellow" }}>Institute</span>
//           </Box>{" "}
//           , we have sent a new password reset code of 6-digit to your email. And
//           this code will expire in ten (10) minutes after which you will have to
//           start all over. Thank you.
//         </Typography>
//       </Box>

//       <div className="auth-container">
//         <h2>Enter Reset Code</h2>
//         <form onSubmit={handleVerify}>
//           <input
//             type="text"
//             placeholder="Enter the 6-digit code"
//             value={code}
//             required
//             onChange={(e) => setCode(e.target.value)}
//             disabled={loading}
//           />
//           <button type="submit" disabled={loading}>Verify Code</button>
//         </form>
//         {loading && <div className="spinner" />}
//         <p className="auth-message">{message}</p>
//       </div>
//     </div>
//   );
// };

// export default VerifyCode;

